import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Empty, Modal, Input, Select, Table, Tag, message, Tooltip } from "antd";
import {
  ReloadOutlined,
  PlusOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  KeyOutlined,
  ApartmentOutlined,
  CheckCircleTwoTone,
  ClockCircleOutlined,
  TeamOutlined,
  DashboardOutlined,
} from "@ant-design/icons";

import DashboardLayout from "../../layout/dashboardLayout/DashboardLayout";
import CrmInvites from "./CrmInvites";
import FtcDashboard from "./FtcDashboard";
import FtcCorrespondenceModal from "./FtcCorrespondenceModal";
import {
  getFtcUsers,
  createFtcUser,
  inviteFtcUser,
  setFtcUserPassword,
  grantFtcOffice,
  revokeFtcOffice,
  getOfficesList,
} from "./controller";
import "./crm.css";

// The FTC template needs to exist before granting; the backend returns 409 with a
// clear message. Surface it as-is so a super-admin knows to seed a template role.
function apiError(res, fallback) {
  return (res && res.json && res.json.detail) || fallback;
}

function OfficeSelect({ offices, value, onChange, placeholder }) {
  return (
    <Select
      mode="multiple"
      allowClear
      showSearch
      style={{ width: "100%" }}
      placeholder={placeholder || "Select offices"}
      value={value}
      onChange={onChange}
      optionFilterProp="label"
      options={offices.map((o) => ({
        value: o.id,
        label: o.practice ? `${o.name} — ${o.practice}` : o.name,
      }))}
      maxTagCount="responsive"
    />
  );
}

export default function FtcUsersPage() {
  const [rows, setRows] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(200);
  // The dashboard leads: what the team did today and what is waiting is the
  // question this page is opened to answer; the roster is administration.
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboardTick, setDashboardTick] = useState(0);
  const [inviteCount, setInviteCount] = useState(0);
  const [reloadInvites, setReloadInvites] = useState(0);

  // modals
  const [createOpen, setCreateOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pwUser, setPwUser] = useState(null); // {id, email}
  const [officesUser, setOfficesUser] = useState(null); // full row
  const [corrUser, setCorrUser] = useState(null); // full row — correspondence access

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFtcUsers();
      setStatus(res.status);
      const next = res.status >= 200 && res.status < 300 && Array.isArray(res.json) ? res.json : [];
      setRows(next);
      // Re-point any open row modal at the refreshed row — otherwise it keeps
      // rendering the pre-save copy (a just-registered mailbox would still show
      // its "no mailbox" warning).
      setCorrUser((cur) => (cur ? next.find((r) => r.id === cur.id) || cur : cur));
    } catch (e) {
      setStatus(0);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    getOfficesList().then((r) => {
      if (r.status >= 200 && r.status < 300 && Array.isArray(r.json)) setOffices(r.json);
    });
  }, [load]);

  const columns = useMemo(
    () => [
      {
        title: "FTC user",
        render: (_, r) => (
          <div className="crm-cell-practice">
            <span className="crm-avatar-sm office"><SafetyCertificateOutlined /></span>
            <div className="crm-cell-stack">
              <div className="crm-name">{r.name || r.email}</div>
              <div className="crm-sub">{r.email}</div>
            </div>
          </div>
        ),
      },
      {
        title: "Verified",
        width: 110,
        align: "center",
        render: (_, r) =>
          r.email_verified ? (
            <Tooltip title="Can log in">
              <CheckCircleTwoTone twoToneColor="#52c41a" />
            </Tooltip>
          ) : (
            <Tooltip title="Email not verified — invited user hasn't finished signup">
              <ClockCircleOutlined style={{ color: "#faad14" }} />
            </Tooltip>
          ),
      },
      {
        title: "Office access",
        render: (_, r) =>
          r.offices && r.offices.length ? (
            <div className="crm-tag-wrap">
              {r.offices.map((o) => (
                <Tag key={o.id}>{o.name}</Tag>
              ))}
            </div>
          ) : (
            <span className="crm-sub">No offices</span>
          ),
      },
      {
        // Correspondence access is a DIFFERENT grant from "Office access" beside
        // it — this one says who they may email. Shown separately so nobody reads
        // a task-queue change as also widening who a person can write to.
        title: "Correspondence",
        width: 200,
        render: (_, r) => (
          !r.mailbox ? (
            <Tooltip title="No mailbox registered — this FTC can't send or receive">
              <Tag color="warning">No mailbox</Tag>
            </Tooltip>
          ) : (
            <div className="crm-cell-stack">
              <div className="crm-sub">{r.mailbox.mailbox_address}</div>
              <div className="crm-sub">
                {r.assignment_count
                  ? `${r.assignment_count} clinic${r.assignment_count === 1 ? "" : "s"}`
                  : "No clinics"}
              </div>
            </div>
          )
        ),
      },
      {
        title: "",
        key: "actions",
        width: 340,
        align: "right",
        render: (_, r) => (
          <div className="crm-row-actions">
            <Button size="small" icon={<ApartmentOutlined />} onClick={() => setOfficesUser(r)}>
              Offices
            </Button>
            <Button size="small" icon={<MailOutlined />} onClick={() => setCorrUser(r)}>
              Correspondence
            </Button>
            <Button size="small" icon={<KeyOutlined />} onClick={() => setPwUser(r)}>
              Password
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  const header = (
    <div className="crm-page-head">
      <div className="crm-page-head-left">
        <span className="crm-page-icon"><SafetyCertificateOutlined /></span>
        <div>
          <div className="crm-page-title">FTC Users</div>
          <div className="crm-page-sub">Provision insurance-verification personnel &amp; their office access</div>
        </div>
      </div>
      <div className="crm-page-actions">
        <Button icon={<MailOutlined />} onClick={() => setInviteOpen(true)}>
          Invite
        </Button>
        <Button icon={<PlusOutlined />} type="primary" onClick={() => setCreateOpen(true)}>
          Create user
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            if (activeTab === "invites") setReloadInvites((x) => x + 1);
            else if (activeTab === "dashboard") setDashboardTick((x) => x + 1);
            else load();
          }}
          loading={loading}
        >
          Refresh
        </Button>
      </div>
    </div>
  );

  if (status === 403) {
    return (
      <DashboardLayout>
        <div className="crm-page">
          {header}
          <div className="crm-table-card crm-403">
            <Empty description="Super-admin access only." />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="crm-page">
        {header}

        <div className="crm-tabs" role="tablist" aria-label="FTC views">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "dashboard"}
            className={`crm-tab${activeTab === "dashboard" ? " is-active" : ""}`}
            onClick={() => setActiveTab("dashboard")}
          >
            <DashboardOutlined /> Dashboard
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "users"}
            className={`crm-tab${activeTab === "users" ? " is-active" : ""}`}
            onClick={() => setActiveTab("users")}
          >
            <TeamOutlined /> FTC users <span className="crm-tab-badge">{rows.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "invites"}
            className={`crm-tab${activeTab === "invites" ? " is-active" : ""}`}
            onClick={() => setActiveTab("invites")}
          >
            <MailOutlined /> Invites <span className="crm-tab-badge">{inviteCount}</span>
          </button>
        </div>

        {activeTab === "dashboard" ? <FtcDashboard reloadTick={dashboardTick} /> : null}

        <div style={{ display: activeTab === "users" ? "block" : "none" }}>
          <div className="crm-table-card">
            <Table
              rowKey="id"
              columns={columns}
              dataSource={rows}
              loading={loading}
              locale={{ emptyText: <Empty description="No FTC users yet. Create or invite one." /> }}
              pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ["10", "25", "50", "100"] }}
            />
          </div>
        </div>

        <div style={{ display: activeTab === "invites" ? "block" : "none" }}>
          <CrmInvites type="ftc" reloadTick={reloadInvites} onCount={setInviteCount} />
        </div>

        <CreateFtcModal
          open={createOpen}
          offices={offices}
          onClose={() => setCreateOpen(false)}
          onDone={() => { setCreateOpen(false); load(); }}
        />
        <InviteFtcModal
          open={inviteOpen}
          offices={offices}
          onClose={() => setInviteOpen(false)}
          onDone={() => {
            setInviteOpen(false);
            setActiveTab("invites");
            setReloadInvites((x) => x + 1);
          }}
        />
        <SetPasswordModal
          user={pwUser}
          onClose={() => setPwUser(null)}
          onDone={() => setPwUser(null)}
        />
        <ManageOfficesModal
          user={officesUser}
          offices={offices}
          onClose={() => setOfficesUser(null)}
          onChanged={(updated) => {
            setRows((rs) => rs.map((x) => (x.id === updated.id ? updated : x)));
            setOfficesUser(updated);
          }}
        />
        {corrUser ? (
          <FtcCorrespondenceModal
            user={corrUser}
            offices={offices}
            onClose={() => setCorrUser(null)}
            // Refetch rather than patch locally: the mailbox and grant count are
            // server-derived, and the modal changes both.
            onChanged={load}
          />
        ) : null}
      </div>
    </DashboardLayout>
  );
}

function CreateFtcModal({ open, offices, onClose, onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [officeIds, setOfficeIds] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setEmail(""); setPassword(""); setOfficeIds([]); }
  }, [open]);

  const submit = async () => {
    if (!email.trim() || !password) {
      message.error("Email and password are required.");
      return;
    }
    setBusy(true);
    const res = await createFtcUser(email.trim(), password, officeIds);
    setBusy(false);
    if (res.status >= 200 && res.status < 300) {
      message.success("FTC user created — they can log in now.");
      onDone();
    } else {
      message.error(apiError(res, "Could not create the FTC user."));
    }
  };

  return (
    <Modal
      title="Create FTC user"
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText="Create"
      confirmLoading={busy}
      zIndex={10050}
      destroyOnClose
    >
      <p className="crm-sub">
        Creates a login-ready account (email verified automatically) with
        insurance-verification access to the selected offices.
      </p>
      <label className="crm-field-label">Email</label>
      <Input placeholder="person@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
      <label className="crm-field-label" style={{ marginTop: 12 }}>Temporary password</label>
      <Input.Password autoComplete="new-password" placeholder="Set an initial password" value={password} onChange={(e) => setPassword(e.target.value)} />
      <label className="crm-field-label" style={{ marginTop: 12 }}>Office access</label>
      <OfficeSelect offices={offices} value={officeIds} onChange={setOfficeIds} />
    </Modal>
  );
}

function InviteFtcModal({ open, offices, onClose, onDone }) {
  const [email, setEmail] = useState("");
  const [officeIds, setOfficeIds] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setEmail(""); setOfficeIds([]); }
  }, [open]);

  const submit = async () => {
    if (!email.trim()) {
      message.error("Email is required.");
      return;
    }
    setBusy(true);
    const res = await inviteFtcUser(email.trim(), officeIds);
    setBusy(false);
    if (res.status >= 200 && res.status < 300) {
      message.success(
        res.json && res.json.email_sent
          ? "Invite sent."
          : "Invite created (email couldn't be sent — check the template)."
      );
      onDone();
    } else {
      message.error(apiError(res, "Could not create the invite."));
    }
  };

  return (
    <Modal
      title="Invite FTC user"
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText="Send invite"
      confirmLoading={busy}
      zIndex={10050}
      destroyOnClose
    >
      <p className="crm-sub">
        Sends a signup link. The invited person sets their own password and confirms
        their email; the selected offices are granted on signup.
      </p>
      <label className="crm-field-label">Email</label>
      <Input placeholder="person@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
      <label className="crm-field-label" style={{ marginTop: 12 }}>Office access</label>
      <OfficeSelect offices={offices} value={officeIds} onChange={setOfficeIds} />
    </Modal>
  );
}

function SetPasswordModal({ user, onClose, onDone }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setPassword(""); }, [user]);

  const submit = async () => {
    if (!password) {
      message.error("Enter a new password.");
      return;
    }
    setBusy(true);
    const res = await setFtcUserPassword(user.id, password);
    setBusy(false);
    if (res.status >= 200 && res.status < 300) {
      message.success("Password updated.");
      onDone();
    } else {
      message.error(apiError(res, "Could not update the password."));
    }
  };

  return (
    <Modal
      title={user ? `Set password — ${user.email}` : "Set password"}
      open={!!user}
      onCancel={onClose}
      onOk={submit}
      okText="Update password"
      confirmLoading={busy}
      zIndex={10050}
      destroyOnClose
    >
      <label className="crm-field-label">New password</label>
      <Input.Password autoComplete="new-password" placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} />
    </Modal>
  );
}

function ManageOfficesModal({ user, offices, onClose, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const [addIds, setAddIds] = useState([]);
  const [addBusy, setAddBusy] = useState(false);

  useEffect(() => { setAddIds([]); }, [user]);

  if (!user) return null;

  const current = user.offices || [];
  const currentIds = new Set(current.map((o) => o.id));
  const addable = offices.filter((o) => !currentIds.has(o.id));

  const revoke = async (officeId) => {
    setBusyId(officeId);
    const res = await revokeFtcOffice(user.id, officeId);
    setBusyId(null);
    if (res.status >= 200 && res.status < 300) {
      onChanged(res.json);
    } else {
      message.error(apiError(res, "Could not remove the office."));
    }
  };

  const grant = async () => {
    if (!addIds.length) return;
    setAddBusy(true);
    let latest = null;
    for (const officeId of addIds) {
      const res = await grantFtcOffice(user.id, officeId);
      if (res.status >= 200 && res.status < 300) {
        latest = res.json;
      } else {
        message.error(apiError(res, "Could not add an office."));
        break;
      }
    }
    setAddBusy(false);
    if (latest) {
      onChanged(latest);
      setAddIds([]);
      message.success("Office access updated.");
    }
  };

  return (
    <Modal
      title={`Office access — ${user.email}`}
      open={!!user}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Done</Button>}
      zIndex={10050}
      destroyOnClose
    >
      <label className="crm-field-label">Current offices</label>
      <div className="crm-tag-wrap" style={{ marginBottom: 12 }}>
        {current.length ? (
          current.map((o) => (
            <Tag
              key={o.id}
              closable
              onClose={(e) => { e.preventDefault(); revoke(o.id); }}
              style={busyId === o.id ? { opacity: 0.5 } : undefined}
            >
              {o.name}
            </Tag>
          ))
        ) : (
          <span className="crm-sub">No offices yet.</span>
        )}
      </div>
      <label className="crm-field-label">Add offices</label>
      <div style={{ display: "flex", gap: 8 }}>
        <OfficeSelect offices={addable} value={addIds} onChange={setAddIds} placeholder="Add office access" />
        <Button type="primary" onClick={grant} loading={addBusy} disabled={!addIds.length}>
          Add
        </Button>
      </div>
    </Modal>
  );
}
